import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { LayoutDashboard, ClipboardList, Users, BarChart3, FileText, Tag, Image as ImageIcon, Star, Settings, Package, MessageCircle, Inbox as InboxIcon, FolderOpen, IndianRupee, AlertTriangle, UserPlus, TrendingUp, ShieldAlert, Target, FileSpreadsheet, Clock, Mail, Wallet, Send, Shield, Database, Megaphone, BookOpen } from "lucide-react";
import { api, inr, STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { ServicesEditor, PortfolioEditor, ReviewsEditor, SettingsEditor, CouponsEditor, ImageUploader, PdfUploader } from "./admin/CmsEditors";
import AdminInbox from "./AdminInbox";
import MediaLibrary from "./admin/MediaLibrary";
import PoachingAudit from "./admin/PoachingAudit";
import SalesLeads from "./admin/SalesLeads";
import Quotations from "./admin/Quotations";
import TasksFollowUps from "./admin/TasksFollowUps";
import EmailAdmin from "./admin/EmailAdmin";
import Projects from "./admin/Projects";
import ProjectWorkspace from "./admin/ProjectWorkspace";
import Invoices from "./admin/Invoices";
import AdminWallet from "./admin/Wallet";
import CommandCenter from "./admin/CommandCenter";
import MobileCommandCenter from "./admin/MobileCommandCenter";
import { Capacitor } from "@capacitor/core";
import Reports from "./admin/Reports";
import Marketing from "./admin/Marketing";
import MarketingOS from "./admin/MarketingOS";
import SecurityCenter from "./admin/SecurityCenter";
import AISalesDashboard from "./admin/AISalesDashboard";
import AISettings from "./admin/AISettings";
import AIKnowledge from "./admin/AIKnowledge";

const COLORS = ["#F7941D", "#1a3c5e", "#D32F2F", "#0EA5E9"];

function Overview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_orders: 0, today_orders: 0, today_revenue: 0, pending_assignment: 0, overdue_orders: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [fuStats, setFuStats] = useState({ due_now: 0 });
  const [quoteStats, setQuoteStats] = useState({ by_status: {} });
  useEffect(() => {
    const load = () => {
      api.get("/admin/dashboard").then(r => setStats(r.data));
      api.get("/admin/analytics").then(r => setAnalytics(r.data));
      api.get("/sales/follow-ups/stats").then(r => setFuStats(r.data || {})).catch(() => {});
      api.get("/sales/quotations/stats").then(r => setQuoteStats(r.data || {})).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="space-y-5" data-testid="admin-overview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Overview</h2>
          <p className="text-xs text-gray-500 mt-0.5">Live · refreshes every 60s</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/system-guide.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-system-guide"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded-lg"
            title="Download the complete System Guide PDF (features, APIs, tech stack, test scenarios)"
          >
            <BookOpen className="w-3.5 h-3.5" /> System Guide PDF
          </a>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-emerald-700 font-bold">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            LIVE
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Total Orders"
          value={stats.total_orders}
          icon={Package}
          tone="blue"
          testId="stat-total-orders"
          onClick={() => navigate("/admin/orders")}
        />
        <StatCard
          label="Today Orders"
          value={stats.today_orders}
          icon={ClipboardList}
          tone="blue"
          testId="stat-today-orders"
          onClick={() => navigate("/admin/orders")}
        />
        <StatCard
          label="Today Revenue"
          value={inr(stats.today_revenue)}
          icon={IndianRupee}
          tone="orange"
          testId="stat-today-revenue"
          onClick={() => navigate("/admin/reports")}
        />
        <StatCard
          label="Pending Assignment"
          value={stats.pending_assignment}
          icon={UserPlus}
          tone={stats.pending_assignment > 0 ? "amber" : "green"}
          testId="stat-pending-assignment"
          onClick={() => navigate("/admin/orders?status=requirements_submitted")}
        />
        <StatCard
          label="Overdue Orders"
          value={stats.overdue_orders}
          icon={AlertTriangle}
          tone={stats.overdue_orders > 0 ? "red" : "green"}
          testId="stat-overdue"
          onClick={() => navigate("/admin/orders?overdue=1")}
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Follow-Ups Due"
          value={fuStats.due_now || 0}
          icon={Clock}
          tone={fuStats.due_now > 0 ? "amber" : "green"}
          testId="stat-followups-due"
          onClick={() => navigate("/admin/follow-ups")}
        />
        <StatCard
          label="Pending Quotations"
          value={(quoteStats.by_status?.draft?.count || 0) + (quoteStats.by_status?.sent?.count || 0)}
          icon={FileSpreadsheet}
          tone="blue"
          testId="stat-quotes-pending"
          onClick={() => navigate("/admin/quotations?status=sent")}
        />
        <StatCard
          label="Accepted Quotes"
          value={quoteStats.by_status?.accepted?.count || 0}
          icon={TrendingUp}
          tone="green"
          testId="stat-quotes-accepted"
          onClick={() => navigate("/admin/quotations?status=accepted")}
        />
        <StatCard
          label="Quote Value (Sent)"
          value={inr(quoteStats.total_value || 0)}
          icon={IndianRupee}
          tone="orange"
          testId="stat-quote-value"
          onClick={() => navigate("/admin/quotations?status=sent")}
        />
      </div>

      {analytics && (
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-[#1a3c5e] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                Revenue (Last 30 Days)
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={analytics.revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" stroke="#1a3c5e" fontSize={11} />
                <YAxis stroke="#1a3c5e" fontSize={11} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#F7941D" strokeWidth={3} dot={{ fill: "#F7941D" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
            <h3 className="font-display font-bold text-[#1a3c5e] mb-2">Orders by Package</h3>
            {analytics.package_chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={analytics.package_chart} dataKey="value" nameKey="name" outerRadius={80} label>
                    {analytics.package_chart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-gray-500 py-10 text-center">No paid orders yet.</div>}
            <div className="text-xs text-gray-500 mt-2">Top package: <span className="font-semibold text-[#F7941D]">{analytics.top_package}</span> · AOV: {inr(analytics.avg_order_value)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const STAT_TONES = {
  blue:   { bg: "from-[#1a3c5e]/5 to-[#1a3c5e]/0",     icon: "bg-[#1a3c5e]/10 text-[#1a3c5e]",     value: "text-[#1a3c5e]" },
  orange: { bg: "from-[#F7941D]/8 to-[#F7941D]/0",     icon: "bg-[#F7941D]/15 text-[#F7941D]",     value: "text-[#F7941D]" },
  amber:  { bg: "from-amber-100 to-amber-50/0",        icon: "bg-amber-100 text-amber-700",        value: "text-amber-600" },
  red:    { bg: "from-red-100 to-red-50/0",            icon: "bg-red-100 text-red-700",            value: "text-red-600" },
  green:  { bg: "from-emerald-100 to-emerald-50/0",    icon: "bg-emerald-100 text-emerald-700",    value: "text-emerald-600" },
};

function StatCard({ label, value, icon: Icon, tone = "blue", testId, onClick }) {
  const t = STAT_TONES[tone] || STAT_TONES.blue;
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testId}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`relative overflow-hidden rounded-2xl border border-[#1a3c5e]/10 bg-gradient-to-br ${t.bg} bg-white p-4 sm:p-5 transition-shadow ${
        clickable ? "cursor-pointer hover:shadow-md hover:border-[#F7941D]/40 focus:outline-none focus:ring-2 focus:ring-[#F7941D]/50" : "hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
          <div className={`font-display font-extrabold mt-1 text-2xl sm:text-3xl ${t.value} break-words`}>{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.icon} flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") || "");
  const [search, setSearch] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [designers, setDesigners] = useState([]);
  const overdueOnly = searchParams.get("overdue") === "1";
  const load = () => api.get("/admin/orders" + (filter ? `?status=${filter}` : "")).then(r => setOrders(r.data));
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/designers").then(r => setDesigners(r.data)); }, []);

  const clearQuickFilter = () => { setFilter(""); setSearchParams({}); };

  const filtered = orders.filter(o => {
    if (overdueOnly) {
      const isOverdue = o.deadline && new Date(o.deadline) < new Date() &&
        !["delivered", "completed", "cancelled"].includes(o.status);
      if (!isOverdue) return false;
    }
    return !search || o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.id.includes(search) || o.service_name?.toLowerCase().includes(search.toLowerCase());
  });

  const assign = async (designerId) => {
    try {
      await api.post(`/admin/orders/${assignFor}/assign`, { designer_id: designerId });
      toast.success("Designer assigned!");
      setAssignFor(null);
      load();
    } catch (e) { toast.error("Assign failed"); }
  };

  const markCompleted = async (oid) => {
    if (!window.confirm("Mark this order as completed? Customer will be notified.")) return;
    try {
      await api.post(`/admin/orders/${oid}/mark-completed`, { note: "" });
      toast.success("Order marked completed — customer notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("version_notes", uploadNotes || "Admin uploaded delivery");
      await api.post(`/admin/orders/${uploadFor}/upload-delivery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Delivery uploaded — customer notified");
      setUploadFor(null); setUploadFile(null); setUploadNotes("");
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Orders</h2>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." data-testid="admin-orders-search" className="ml-auto border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-orders-filter" className="border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white">
          <option value="">All status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#FEF3E2]/50">
              <tr className="text-xs uppercase text-[#1a3c5e]/70 tracking-wider">
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Service</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Designer</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} className="border-t border-[#1a3c5e]/10 hover:bg-[#FEF3E2]/30" data-testid={`admin-order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0,8)}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-[#1a3c5e]">{o.customer_name}</div><div className="text-xs text-gray-500">{o.customer_email}</div></td>
                  <td className="px-4 py-3">{o.service_name}</td>
                  <td className="px-4 py-3 font-semibold text-[#F7941D]">{inr(o.amount)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span></td>
                  <td className="px-4 py-3 text-xs">{o.designer_name || <span className="text-amber-600 font-semibold">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      {!o.designer_id && o.status !== "pending_payment" && o.status !== "completed" && o.status !== "cancelled" && (
                        <button onClick={() => setAssignFor(o.id)} data-testid={`assign-btn-${o.id}`} className="px-2.5 py-1 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-xs font-semibold rounded-lg">Assign</button>
                      )}
                      {["assigned", "in_progress", "delivered", "requirements_submitted"].includes(o.status) && (
                        <button onClick={() => { setUploadFor(o.id); setUploadFile(null); setUploadNotes(""); }} data-testid={`upload-delivery-btn-${o.id}`} className="px-2.5 py-1 bg-[#F7941D] hover:bg-[#e0861a] text-white text-xs font-semibold rounded-lg">Upload</button>
                      )}
                      {o.status === "delivered" && (
                        <button onClick={() => markCompleted(o.id)} data-testid={`complete-btn-${o.id}`} className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg">Complete</button>
                      )}
                      {o.customer_phone && (
                        <button
                          onClick={() => navigate(`/admin/inbox?search=${encodeURIComponent(o.customer_phone.replace(/\D/g, ''))}&name=${encodeURIComponent(o.customer_name || '')}`)}
                          className="px-2.5 py-1 text-xs text-[#25D366] font-semibold"
                          title="Open in Command Center (official WhatsApp API)"
                        >
                          WA
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-12">No orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {assignFor && (() => {
        // Suggest the least-busy verified designer first — admin still has to click to confirm.
        const sorted = [...designers].sort((a, b) => {
          const aVerified = a.profile?.is_verified ? 0 : 1;
          const bVerified = b.profile?.is_verified ? 0 : 1;
          if (aVerified !== bVerified) return aVerified - bVerified;
          return (a.active_orders || 0) - (b.active_orders || 0);
        });
        const suggestedId = sorted.find(d => d.profile?.is_verified)?.id || sorted[0]?.id;
        return (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAssignFor(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Assign Designer</h3>
              <p className="text-xs text-gray-500 mb-4">Sorted by lowest active workload — the top pick is our suggestion.</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {sorted.map(d => (
                  <button
                    key={d.id}
                    onClick={() => assign(d.id)}
                    data-testid={`assign-designer-${d.id}`}
                    className={`w-full text-left p-3 rounded-xl border flex justify-between items-center ${
                      d.id === suggestedId ? "border-[#F7941D] bg-[#FEF3E2]/60" : "border-[#1a3c5e]/10 hover:bg-[#FEF3E2]/40"
                    }`}
                  >
                    <div>
                      <div className="font-semibold text-[#1a3c5e] flex items-center gap-1.5">
                        {d.name}
                        {d.id === suggestedId && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-[#F7941D] text-white">Suggested</span>}
                        {!d.profile?.is_verified && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">Unverified</span>}
                      </div>
                      <div className="text-xs text-gray-500">{d.city || "—"} · {d.profile?.skills?.join(", ") || "No skills listed"}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-[#FEF3E2] text-[#F7941D] font-bold">{d.active_orders} active</span>
                  </button>
                ))}
                {designers.length === 0 && <p className="text-sm text-gray-500">No designers — add some via Register.</p>}
              </div>
              <button onClick={() => setAssignFor(null)} className="mt-4 text-sm text-gray-500 hover:underline">Cancel</button>
            </div>
          </div>
        );
      })()}

      {uploadFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !uploading && setUploadFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Upload Delivery</h3>
            <p className="text-xs text-gray-500 mb-4">File will be saved against order #{uploadFor.slice(0,8)}. Customer is notified automatically.</p>
            <input
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              data-testid="admin-upload-file"
              className="w-full text-sm mb-3 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#FEF3E2] file:text-[#F7941D] file:font-semibold"
            />
            <textarea
              rows={3}
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              placeholder="Version notes (e.g. Final 2D plan v3 — included roof layout & utility room revisions)"
              data-testid="admin-upload-notes"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setUploadFor(null)} disabled={uploading} className="px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-xl">Cancel</button>
              <button onClick={submitUpload} disabled={uploading || !uploadFile} data-testid="admin-upload-submit" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white font-semibold rounded-xl">{uploading ? "Uploading..." : "Upload"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Designers() {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", phone: "", city: "", bio: "", skills: "", auto_verify: true, send_welcome_email: true });
  const [createResult, setCreateResult] = useState(null);
  const load = () => api.get("/admin/designers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const toggleVerify = async (d) => {
    const desired = !(d.profile?.is_verified);
    try {
      await api.put(`/admin/designers/${d.id}/verify`, { is_verified: desired });
      toast.success(desired ? "Designer verified · login enabled" : "Verification revoked");
      load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const createDesigner = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email) { toast.error("Name + email required"); return; }
    setCreating(true);
    try {
      const payload = {
        name: createForm.name.trim(),
        email: createForm.email.trim().toLowerCase(),
        phone: createForm.phone.trim(),
        city: createForm.city.trim(),
        bio: createForm.bio.trim(),
        skills: createForm.skills.split(",").map(s => s.trim()).filter(Boolean),
        auto_verify: createForm.auto_verify,
        send_welcome_email: createForm.send_welcome_email,
      };
      const r = await api.post("/admin/designers", payload);
      setCreateResult(r.data);
      const emailNote = r.data.welcome_email?.sent ? "Welcome email sent" : (r.data.welcome_email?.error || "Welcome email not sent");
      toast.success(`Designer created · ${emailNote}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create designer");
    } finally {
      setCreating(false);
    }
  };

  const resetCreate = () => {
    setShowCreate(false);
    setCreateForm({ name: "", email: "", phone: "", city: "", bio: "", skills: "", auto_verify: true, send_welcome_email: true });
    setCreateResult(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Designers</h2>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="open-create-designer-btn"
          className="bg-[#F7941D] hover:bg-[#e87f00] text-white font-bold px-4 py-2 rounded-lg text-sm"
        >+ New Designer</button>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-designer-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            {!createResult ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl font-bold text-[#1a3c5e]">Create Designer</h3>
                  <button onClick={resetCreate} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="close-create-designer-btn">×</button>
                </div>
                <form onSubmit={createDesigner} className="space-y-3">
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Full name *" value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} data-testid="new-designer-name" required />
                  <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Email *" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} data-testid="new-designer-email" required />
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Phone (optional)" value={createForm.phone} onChange={e => setCreateForm({...createForm, phone: e.target.value})} data-testid="new-designer-phone" />
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="City" value={createForm.city} onChange={e => setCreateForm({...createForm, city: e.target.value})} data-testid="new-designer-city" />
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Short bio" value={createForm.bio} onChange={e => setCreateForm({...createForm, bio: e.target.value})} data-testid="new-designer-bio" />
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Skills (comma separated: AutoCAD, Revit, SketchUp)" value={createForm.skills} onChange={e => setCreateForm({...createForm, skills: e.target.value})} data-testid="new-designer-skills" />
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={createForm.auto_verify} onChange={e => setCreateForm({...createForm, auto_verify: e.target.checked})} data-testid="new-designer-auto-verify" />
                    Auto-verify (designer can login immediately)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={createForm.send_welcome_email} onChange={e => setCreateForm({...createForm, send_welcome_email: e.target.checked})} data-testid="new-designer-send-email" />
                    Send welcome email with login URL + password
                  </label>
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={resetCreate} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-bold">Cancel</button>
                    <button type="submit" disabled={creating} className="flex-1 bg-[#F7941D] text-white rounded-lg py-2 text-sm font-bold disabled:opacity-50" data-testid="submit-create-designer-btn">{creating ? "Creating…" : "Create Designer"}</button>
                  </div>
                </form>
              </>
            ) : (
              <div data-testid="create-designer-success">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl font-bold text-emerald-700">Designer Created ✓</h3>
                  <button onClick={resetCreate} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-3 text-sm">
                  <div><strong>Email:</strong> {createResult.designer.email}</div>
                  <div className="mt-1"><strong>Temp password:</strong> <code className="bg-white px-2 py-1 rounded font-mono text-xs">{createResult.temp_password}</code></div>
                  <div className="mt-1"><strong>Login URL:</strong> <a href={createResult.login_url} target="_blank" rel="noreferrer" className="text-[#1a3c5e] underline">{createResult.login_url}</a></div>
                </div>
                <div className={`rounded-lg p-3 text-sm ${createResult.welcome_email?.sent ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {createResult.welcome_email?.sent
                    ? <>✉️ Welcome email sent · Resend id: <code className="text-xs">{createResult.welcome_email?.resend_id}</code></>
                    : <>⚠️ Welcome email NOT sent: {createResult.welcome_email?.error || createResult.welcome_email?.reason || "unknown"}. Please share the credentials above manually.</>}
                </div>
                <button onClick={resetCreate} className="w-full mt-4 bg-[#1a3c5e] text-white rounded-lg py-2 text-sm font-bold" data-testid="close-success-btn">Done</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {list.map(d => (
          <div key={d.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5" data-testid={`designer-row-${d.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#1a3c5e] text-white font-bold flex items-center justify-center">{d.name[0]}</div>
              <div>
                <div className="font-display font-bold text-[#1a3c5e]">{d.name}</div>
                <div className="text-xs text-gray-500">{d.email}</div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-xs text-gray-500">Active</div>
                <div className="font-display text-2xl font-bold text-[#F7941D]">{d.active_orders}</div>
              </div>
            </div>
            {d.profile && (
              <div className="mt-3 text-xs text-gray-600">
                {d.profile.skills?.length > 0 && <div className="flex flex-wrap gap-1 mb-1.5">{d.profile.skills.map((s,i) => <span key={i} className="px-2 py-0.5 bg-[#FEF3E2] text-[#F7941D] rounded text-[10px] font-bold">{s}</span>)}</div>}
                <div>★ {d.profile.avg_rating || 0} · {d.profile.total_orders || 0} total orders</div>
              </div>
            )}
            <div className="mt-3 flex items-center justify-between pt-3 border-t border-gray-100">
              <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${d.profile?.is_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {d.profile?.is_verified ? "Verified" : "Pending"}
              </span>
              <button
                onClick={() => toggleVerify(d)}
                data-testid={`verify-toggle-${d.id}`}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg ${d.profile?.is_verified ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
              >
                {d.profile?.is_verified ? "Revoke" : "Verify Now"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const BLOG_EMPTY_FORM = { title: "", slug: "", content: "", category: "Vastu", meta_title: "", meta_desc: "", cover_image: "", status: "published" };

function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(BLOG_EMPTY_FORM);
  const [show, setShow] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = creating, else editing this slug
  // B5: was reading the PUBLIC /blog endpoint (status=published only) — a draft was
  // invisible here too, not just missing an edit button.
  const load = () => api.get("/admin/blog").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlug) {
        await api.put(`/admin/blog/${editingSlug}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", form);
        toast.success(form.status === "draft" ? "Draft saved" : "Post published");
      }
      setShow(false); setEditingSlug(null); setForm(BLOG_EMPTY_FORM); load();
    } catch { toast.error(editingSlug ? "Failed to update" : "Failed to publish"); }
  };
  const startEdit = (p) => {
    setForm({ title: p.title, slug: p.slug, content: p.content, category: p.category || "Vastu", meta_title: p.meta_title || "", meta_desc: p.meta_desc || "", cover_image: p.cover_image || "", status: p.status || "published" });
    setEditingSlug(p.slug);
    setShow(true);
  };
  const startCreate = () => { setForm(BLOG_EMPTY_FORM); setEditingSlug(null); setShow(true); };
  const remove = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${slug}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Blog Posts</h2>
        <button onClick={() => (show ? (setShow(false), setEditingSlug(null)) : startCreate())} data-testid="new-blog-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl text-sm">{show ? "Cancel" : "+ New Post"}</button>
      </div>
      {show && (
        <form onSubmit={submit} className="bg-white rounded-2xl p-5 border border-[#1a3c5e]/10 space-y-3">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value, slug: editingSlug ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')})} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
          <input required disabled={!!editingSlug} placeholder="Slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500" />
          <input placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({...form, cover_image: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
          <input placeholder="Meta description" value={form.meta_desc} onChange={(e) => setForm({...form, meta_desc: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2" />
          <textarea required rows={10} placeholder="Content..." value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2 font-mono text-sm" />
          <p className="text-xs text-gray-500 -mt-1">
            Formatting: <code className="bg-gray-100 px-1 rounded">## Heading</code>, <code className="bg-gray-100 px-1 rounded">### Subheading</code>, <code className="bg-gray-100 px-1 rounded">- item</code> / <code className="bg-gray-100 px-1 rounded">1. item</code> lists, <code className="bg-gray-100 px-1 rounded">&gt; quote</code>, <code className="bg-gray-100 px-1 rounded">**bold**</code>, <code className="bg-gray-100 px-1 rounded">*italic*</code>, <code className="bg-gray-100 px-1 rounded">[text](url)</code> link, <code className="bg-gray-100 px-1 rounded">![caption](image url)</code>, <code className="bg-gray-100 px-1 rounded">---</code> divider.
            Paste a YouTube or Instagram Reel/Post link on its own line to embed it as a video player automatically.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-[#1a3c5e]">Status:</span>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="border border-gray-300 rounded-lg px-2 py-1">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
          <button type="submit" className="px-5 py-2 bg-[#1a3c5e] text-white font-semibold rounded-xl">{editingSlug ? "Save changes" : (form.status === "draft" ? "Save draft" : "Publish")}</button>
        </form>
      )}
      <div className="space-y-2">
        {posts.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-14 h-14 object-cover rounded-lg" />}
              <div>
                <div className="font-semibold text-[#1a3c5e] flex items-center gap-2">
                  {p.title}
                  {p.status === "draft" && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">Draft</span>}
                </div>
                <div className="text-xs text-gray-500">{p.category} · /{p.slug}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => startEdit(p)} data-testid={`edit-blog-${p.slug}`} className="text-[#1a3c5e] text-sm hover:underline">Edit</button>
              <button onClick={() => remove(p.slug)} className="text-red-600 text-sm hover:underline">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const GUIDE_EMPTY_FORM = { title: "", slug: "", description: "", category: "General", cover_image: "", pdf: null, status: "published" };

function GuidesAdmin() {
  const [guides, setGuides] = useState([]);
  const [form, setForm] = useState(GUIDE_EMPTY_FORM);
  const [show, setShow] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = creating, else editing this slug
  const load = () => api.get("/admin/guides").then(r => setGuides(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!editingSlug && !form.pdf?.pdf_file_id) {
      toast.error("Upload a PDF before publishing");
      return;
    }
    const payload = {
      title: form.title,
      slug: form.slug,
      description: form.description,
      category: form.category,
      cover_image: form.cover_image,
      status: form.status,
      ...(form.pdf ? { pdf_file_id: form.pdf.pdf_file_id, file_name: form.pdf.file_name, file_size: form.pdf.file_size } : {}),
    };
    try {
      if (editingSlug) {
        await api.put(`/admin/guides/${editingSlug}`, payload);
        toast.success("Guide updated");
      } else {
        await api.post("/admin/guides", payload);
        toast.success(form.status === "draft" ? "Draft saved" : "Guide published");
      }
      setShow(false); setEditingSlug(null); setForm(GUIDE_EMPTY_FORM); load();
    } catch (err) { toast.error(err.response?.data?.detail || (editingSlug ? "Failed to update" : "Failed to publish")); }
  };
  const startEdit = (g) => {
    setForm({
      title: g.title, slug: g.slug, description: g.description || "", category: g.category || "General",
      cover_image: g.cover_image || "", status: g.status || "published",
      pdf: g.pdf_file_id ? { pdf_file_id: g.pdf_file_id, file_name: g.file_name, file_size: g.file_size } : null,
    });
    setEditingSlug(g.slug);
    setShow(true);
  };
  const startCreate = () => { setForm(GUIDE_EMPTY_FORM); setEditingSlug(null); setShow(true); };
  const remove = async (slug) => {
    if (!window.confirm("Delete this guide? The PDF will no longer be downloadable.")) return;
    try { await api.delete(`/admin/guides/${slug}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Free PDF Guides</h2>
        <button onClick={() => (show ? (setShow(false), setEditingSlug(null)) : startCreate())} data-testid="new-guide-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl text-sm">{show ? "Cancel" : "+ New Guide"}</button>
      </div>
      {show && (
        <form onSubmit={submit} className="bg-white rounded-2xl p-5 border border-[#1a3c5e]/10 space-y-3">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value, slug: editingSlug ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')})} className="w-full border border-gray-300 rounded-xl px-3 py-2" data-testid="guide-title-input" />
          <input required disabled={!!editingSlug} placeholder="Slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500" data-testid="guide-slug-input" />
          <input placeholder="Category (e.g. Vastu, Construction, Interiors)" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2" data-testid="guide-category-input" />
          <textarea rows={3} placeholder="Short description shown on the guide card" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full border border-gray-300 rounded-xl px-3 py-2" data-testid="guide-description-input" />
          <ImageUploader value={form.cover_image} onChange={(v) => setForm({...form, cover_image: v})} label="Cover Image (optional)" testId="guide-cover" />
          <PdfUploader value={form.pdf} onChange={(v) => setForm({...form, pdf: v})} testId="guide-pdf" />
          <label className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-[#1a3c5e]">Status:</span>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="border border-gray-300 rounded-lg px-2 py-1">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
          <button type="submit" className="px-5 py-2 bg-[#1a3c5e] text-white font-semibold rounded-xl">{editingSlug ? "Save changes" : (form.status === "draft" ? "Save draft" : "Publish")}</button>
        </form>
      )}
      <div className="space-y-2">
        {guides.map(g => (
          <div key={g.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {g.cover_image ? (
                <img src={g.cover_image} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-[#1a3c5e] flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white/60" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-[#1a3c5e] flex items-center gap-2">
                  <span className="truncate">{g.title}</span>
                  {g.status === "draft" && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase flex-shrink-0">Draft</span>}
                </div>
                <div className="text-xs text-gray-500">{g.category} · /{g.slug} · {g.download_count || 0} downloads</div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={() => startEdit(g)} data-testid={`edit-guide-${g.slug}`} className="text-[#1a3c5e] text-sm hover:underline">Edit</button>
              <button onClick={() => remove(g.slug)} className="text-red-600 text-sm hover:underline">Delete</button>
            </div>
          </div>
        ))}
        {guides.length === 0 && <div className="bg-white rounded-2xl p-10 text-center text-gray-500 border border-[#1a3c5e]/10">No guides yet — click "+ New Guide" to upload your first free PDF.</div>}
      </div>
    </div>
  );
}

function CouponsView() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/coupons").then(r => setList(r.data)); }, []);
  return (
    <div className="space-y-4">
      <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Coupons</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map(c => (
          <div key={c.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
            <div className="font-mono font-bold text-lg text-[#F7941D]">{c.code}</div>
            <div className="text-xs text-gray-500 mt-1">{c.discount_percent}% off · Min order {inr(c.min_order)} · {c.uses_left} uses left</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TEMPLATE_BODIES = {
  order_paid_customer: "Hi! Your order {{1}} ({{2}}) is paid. Submit requirements in dashboard to start.",
  designer_assigned_customer: "Designer assigned for order {{1}}! {{2}} will deliver your design soon.",
  delivery_uploaded_customer: "Your order {{1}} delivery is ready. Login to dashboard to review & download.",
  order_completed_customer: "Order {{1}} marked complete! Thanks for choosing HouseDesignWala. All files in dashboard.",
  new_order_designer: "New order assigned: {{1}} — {{2}}. Login to designer dashboard to accept.",
  revision_requested_designer: "Revision requested on order {{1}}. Customer note: {{2}}",
};

function WhatsAppAdmin() {
  const [status, setStatus] = useState(null);
  const [diag, setDiag] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testTpl, setTestTpl] = useState("order_paid_customer");
  const [testParams, setTestParams] = useState("HDW001, Basic Package");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [directBody, setDirectBody] = useState("Test message from HouseDesignWala 👋");
  const [directBusy, setDirectBusy] = useState(false);
  const [directResult, setDirectResult] = useState(null);

  const loadAll = () => {
    api.get("/admin/whatsapp/status").then(r => setStatus(r.data));
    api.get("/admin/whatsapp/diagnostics").then(r => setDiag(r.data)).catch(() => setDiag(null));
  };
  useEffect(() => { loadAll(); }, []);

  const runDirectSend = async () => {
    if (!testPhone) { toast.error("Enter a phone first"); return; }
    setDirectBusy(true); setDirectResult(null);
    try {
      const { data } = await api.post("/admin/whatsapp/test-send", { phone: testPhone, body: directBody });
      setDirectResult(data);
      if (data.ok) toast.success("Sent — check the WhatsApp inbox of the test phone");
      else toast.error(data.reason === "integration_disabled" ? "Integration disabled — set env vars first" : (data.error?.message || data.reason));
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setDirectBusy(false); }
  };

  const sendTest = async () => {
    if (!testPhone) { toast.error("Enter a phone number"); return; }
    setSending(true);
    setLastResult(null);
    try {
      const params = testParams.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/whatsapp/test", { phone: testPhone, template_name: testTpl, params });
      setLastResult(data);
      if (data.enabled && data.ok) toast.success("Message sent — check your WhatsApp");
      else if (!data.enabled) toast.info("WhatsApp is disabled (env vars missing) — see setup instructions above");
      else toast.error("Send failed — check response for details");
    } catch (e) { toast.error("Request failed"); }
    finally { setSending(false); }
  };

  if (!status) return <div className="bg-white rounded-2xl p-10 text-center text-gray-500">Loading…</div>;
  const tplEntries = Object.entries(status.templates_required || {});
  const uniqueTpls = [...new Set(Object.values(status.templates_required || {}))];
  const bodies = status.template_bodies || TEMPLATE_BODIES;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-[#25D366]" /> WhatsApp Notifications
            </h2>
            <p className="text-xs text-gray-500 mt-1">Auto-sends WhatsApp messages on key events using Meta Cloud API (Direct).</p>
          </div>
          <span data-testid="wa-status-badge" className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase ${status.enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {status.enabled ? "Active" : "Setup Required"}
          </span>
        </div>
      </div>

      {/* Diagnostics card */}
      {diag && (
        <div className={`rounded-2xl border p-5 ${diag.overall_ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="wa-diagnostics">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className={`font-bold ${diag.overall_ok ? "text-emerald-800" : "text-rose-800"}`}>
              Integration Diagnostics — {diag.overall_ok ? "✅ Ready to deliver to real WhatsApp" : "⚠️ Issues block real delivery"}
            </h3>
            <button onClick={loadAll} className="text-xs px-3 py-1 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="wa-diag-refresh">Re-check</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            {(diag.checks || []).map(c => (
              <div key={c.name} className={`p-2 rounded-lg ${c.ok ? "bg-white" : "bg-rose-100/60"}`}>
                <div className="font-semibold flex items-center gap-1.5">
                  {c.ok ? <span className="text-emerald-600">✓</span> : <span className="text-rose-600">✗</span>}
                  {c.name}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">{c.detail}</div>
                {!c.ok && c.fix && <div className="text-xs text-rose-700 italic mt-1">→ {c.fix}</div>}
              </div>
            ))}
          </div>
          {diag.meta_api_check && diag.meta_api_check.ok && (
            <div className="mt-3 text-xs text-emerald-900 bg-emerald-100 rounded-lg p-2">
              <span className="font-bold">Live phone:</span> {diag.meta_api_check.display_phone_number} ({diag.meta_api_check.verified_name}) · Quality: {diag.meta_api_check.quality_rating}
            </div>
          )}
          {diag.recent_outbound_stats && Object.keys(diag.recent_outbound_stats).length > 0 && (
            <div className="mt-3 text-xs">
              <span className="font-bold text-gray-700">Recent outbound stats:</span>{" "}
              {Object.entries(diag.recent_outbound_stats).map(([k, v]) => (
                <span key={k} className={`ml-2 px-2 py-0.5 rounded ${k === "sent" ? "bg-emerald-100 text-emerald-700" : (k === "disabled" || k === "failed" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700")}`}>
                  {k}: <strong>{v}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Direct outbound test */}
      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
        <h3 className="font-bold text-[#1a3c5e] mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#25D366]" /> End-to-End Outbound Test</h3>
        <p className="text-xs text-gray-500 mb-3">Sends a free-form text directly to the given phone via Meta API (requires an open 24h window on Meta side, e.g. send a WhatsApp message TO your business first).</p>
        <div className="grid sm:grid-cols-[200px_1fr_auto] gap-2 items-end">
          <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+91 XXXXXXXXXX" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="wa-direct-phone" />
          <input value={directBody} onChange={e => setDirectBody(e.target.value)} placeholder="Message" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="wa-direct-body" />
          <button onClick={runDirectSend} disabled={directBusy} data-testid="wa-direct-send" className="px-4 py-2 bg-[#25D366] hover:bg-[#1ea855] disabled:opacity-50 text-white text-sm font-bold rounded-lg">{directBusy ? "Sending..." : "Send via Meta API"}</button>
        </div>
        {directResult && (
          <pre className={`mt-3 text-xs p-3 rounded-lg overflow-x-auto ${directResult.ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="wa-direct-result">{JSON.stringify(directResult, null, 2)}</pre>
        )}
      </div>

      {!status.enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 space-y-3">
          <h3 className="font-display font-bold text-amber-900">Setup Instructions</h3>
          <ol className="text-sm text-amber-900 space-y-2 list-decimal list-inside">
            <li>Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Meta Business Manager</a> → <span className="font-semibold">WhatsApp Manager → Account tools → Phone numbers</span>. Copy the <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">Phone Number ID</span>.</li>
            <li>Create a <span className="font-semibold">System User</span> in Business Settings → Users → System Users. Assign <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">whatsapp_business_messaging</span> permission. Generate a long-lived access token.</li>
            <li>Copy your <span className="font-semibold">WhatsApp Business Account (WABA) ID</span> from Business Settings → Accounts → WhatsApp Accounts.</li>
            <li>In WhatsApp Manager → Message Templates, create all <strong>{uniqueTpls.length} templates</strong> listed below with category <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">Utility</span> and language <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">en_US</span>. Wait for approval (usually a few minutes).</li>
            <li>Set these env vars in <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">/app/backend/.env</span>:
              <pre className="mt-2 p-3 bg-amber-100 rounded text-[10px] font-mono leading-relaxed overflow-x-auto">{`WHATSAPP_PHONE_NUMBER_ID="123456789012345"
WHATSAPP_ACCESS_TOKEN="EAAxxxxxxxxxxxxx"
WHATSAPP_BUSINESS_ACCOUNT_ID="987654321"
WHATSAPP_API_VERSION="v20.0"`}</pre>
            </li>
            <li>Restart backend: <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">sudo supervisorctl restart backend</span></li>
          </ol>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Required Templates ({uniqueTpls.length})</h3>
        <p className="text-xs text-gray-500 mb-4">Create these in WhatsApp Manager. Category: <strong>Utility</strong>, Language: <strong>en_US</strong>. Use <span className="font-mono">{`{{1}}`}</span>, <span className="font-mono">{`{{2}}`}</span> for parameters.</p>
        <div className="space-y-2">
          {tplEntries.map(([event, tpl]) => (
            <div key={event} className="p-3 rounded-xl border border-[#1a3c5e]/10 bg-[#FEF3E2]/30">
              <div className="flex justify-between flex-wrap gap-2 mb-1">
                <span className="font-mono text-xs text-[#F7941D] font-bold">{tpl}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">on event: {event}</span>
              </div>
              <div className="text-xs text-gray-700">{bodies[tpl] || "Body: 'Update for order {{1}}.'"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Send Test Message</h3>
        <p className="text-xs text-gray-500 mb-4">Verify your setup. Use your own WhatsApp number.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#1a3c5e]">Phone (10 digits or 91XXXXXXXXXX)</label>
            <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="9876543210" data-testid="wa-test-phone" className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1a3c5e]">Template</label>
            <select value={testTpl} onChange={(e) => setTestTpl(e.target.value)} data-testid="wa-test-template" className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              {uniqueTpls.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1a3c5e]">Params (comma-separated)</label>
            <input value={testParams} onChange={(e) => setTestParams(e.target.value)} placeholder="HDW001, Basic Package" data-testid="wa-test-params" className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={sendTest} disabled={sending} data-testid="wa-test-send" className="px-5 py-2 bg-[#25D366] hover:bg-[#1da855] disabled:opacity-50 text-white font-bold rounded-xl flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> {sending ? "Sending..." : "Send Test"}
          </button>
        </div>
        {lastResult && (
          <pre className="mt-4 p-3 bg-gray-50 rounded-lg text-[10px] font-mono overflow-x-auto" data-testid="wa-test-result">{JSON.stringify(lastResult, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

export default function AdminPages() {
  return (
    <Routes>
      <Route element={
        <DashboardLayout
          title="Admin Console"
          testIdPrefix="admin"
          links={[
            { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, group: "Dashboard" },
            { to: "/admin/reports", label: "Reports", icon: BarChart3, group: "Dashboard" },

            { to: "/admin/leads", label: "Sales Pipeline", icon: Target, group: "Sales & CRM" },
            { to: "/admin/quotations", label: "Quotations", icon: FileSpreadsheet, group: "Sales & CRM" },
            { to: "/admin/coupons", label: "Coupons", icon: Tag, group: "Sales & CRM" },

            { to: "/admin/orders", label: "Orders", icon: ClipboardList, group: "Projects & Delivery" },
            { to: "/admin/projects", label: "Projects Board", icon: FolderOpen, group: "Projects & Delivery" },
            { to: "/admin/designers", label: "Designers", icon: Users, group: "Projects & Delivery" },

            { to: "/admin/invoices", label: "Invoices", icon: IndianRupee, group: "Finance" },
            { to: "/admin/wallet", label: "Designer Wallets", icon: Wallet, group: "Finance" },

            { to: "/admin/inbox", label: "Command Center", icon: InboxIcon, group: "Communication" },
            { to: "/admin/whatsapp", label: "WhatsApp Templates", icon: MessageCircle, group: "Communication" },
            { to: "/admin/marketing-os", label: "WhatsApp Broadcast", icon: Megaphone, group: "Communication" },
            { to: "/admin/email", label: "Email", icon: Mail, group: "Communication" },
            { to: "/admin/marketing", label: "Marketing", icon: Send, group: "Communication" },

            { to: "/admin/services", label: "Packages", icon: Package, group: "Content & Catalog" },
            { to: "/admin/media", label: "Media Library", icon: FolderOpen, group: "Content & Catalog" },
            { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon, group: "Content & Catalog" },
            { to: "/admin/reviews", label: "Reviews", icon: Star, group: "Content & Catalog" },
            { to: "/admin/blog", label: "Blog", icon: FileText, group: "Content & Catalog" },
            { to: "/admin/guides", label: "PDF Guides", icon: BookOpen, group: "Content & Catalog" },

            { to: "/admin/ai-sales", label: "AI Sales Dashboard", icon: TrendingUp, group: "AI Tools" },
            { to: "/admin/ai-settings", label: "AI Control Settings", icon: ShieldAlert, group: "AI Tools" },
            { to: "/admin/ai-knowledge", label: "AI Knowledge", icon: Database, group: "AI Tools" },

            { to: "/admin/follow-ups", label: "Tasks & Follow-Ups", icon: Clock, group: "Admin & Security" },
            { to: "/admin/poaching", label: "Anti-Poaching", icon: ShieldAlert, group: "Admin & Security" },
            { to: "/admin/security", label: "Security", icon: Shield, group: "Admin & Security" },
            { to: "/admin/settings", label: "Site Settings", icon: Settings, group: "Admin & Security" },
          ]}
        />
      }>
        <Route index element={<Overview />} />
        <Route path="reports" element={<Reports />} />
        <Route path="leads" element={<SalesLeads />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectWorkspace />} />
        <Route path="quotations" element={<Quotations />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="wallet" element={<AdminWallet />} />
        <Route path="follow-ups" element={<TasksFollowUps />} />
        <Route path="inbox" element={Capacitor.isNativePlatform() ? <MobileCommandCenter /> : <CommandCenter />} />
        <Route path="command-center" element={<Navigate to="/admin/inbox" replace />} />
        <Route path="ai-sales" element={<AISalesDashboard />} />
        <Route path="ai-settings" element={<AISettings />} />
        <Route path="ai-knowledge" element={<AIKnowledge />} />
        <Route path="inbox-classic" element={<AdminInbox />} />
        <Route path="orders" element={<Orders />} />
        <Route path="services" element={<ServicesEditor />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="poaching" element={<PoachingAudit />} />
        <Route path="portfolio" element={<PortfolioEditor />} />
        <Route path="reviews" element={<ReviewsEditor />} />
        <Route path="designers" element={<Designers />} />
        <Route path="blog" element={<BlogAdmin />} />
        <Route path="guides" element={<GuidesAdmin />} />
        <Route path="coupons" element={<CouponsEditor />} />
        <Route path="whatsapp" element={<WhatsAppAdmin />} />
        <Route path="email" element={<EmailAdmin />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="marketing-os" element={<MarketingOS />} />
        <Route path="security" element={<SecurityCenter />} />
        <Route path="settings" element={<SettingsEditor />} />
      </Route>
    </Routes>
  );
}
